import {
  getSurfaceBlock,
  snapshotMapKeys,
  type SurfaceBlockModel,
  type SurfaceBlockTransformer,
} from '@blocksuite/affine-block-surface';
import { ROW_SHARDED_DATABASE_GATE_MESSAGE } from '@blocksuite/affine-model';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { Bound, getCommonBound, type IVec } from '@blocksuite/global/gfx';
import { assertType } from '@blocksuite/global/utils';
import type { BlockStdScope } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import {
  type BlockModel,
  type BlockSnapshot,
  type DocSnapshot,
  DocSnapshotSchema,
  type SnapshotNode,
  type Transformer,
} from '@blocksuite/store';
import { PageStructureWriteError } from '@streamlain/database-identity';
import { Subject } from 'rxjs';

import {
  catalogImageMiddleware,
  createInsertPlaceMiddleware,
  createRegenerateIndexMiddleware,
  createStickerMiddleware,
  replaceIdMiddleware,
  type TemplatePlace,
} from './template-middlewares';
import {
  createRightSidePlacementBound,
  getBlockSnapshotContentBound,
} from './template-placement.js';
/**
 * Those block contains other block's id
 * should defer the loading
 */
const DEFERED_BLOCK = [
  'affine:surface',
  'affine:surface-ref',
  'affine:frame',
] as const;

/**
 * Those block should not be inserted directly
 * it should be merged with current existing block
 */
const MERGE_BLOCK = ['affine:surface', 'affine:page'] as const;

type MergeBlockFlavour = (typeof MERGE_BLOCK)[number];

/**
 * Template type will affect the inserting behaviour
 */
const TEMPLATE_TYPES = ['template', 'sticker'] as const;

type TemplateType = (typeof TEMPLATE_TYPES)[number];

export type SlotBlockPayload = {
  type: 'block';
  data: {
    blockJson: BlockSnapshot;
    parent?: string;
    index?: number;
  };
};

export type SlotPayload =
  | SlotBlockPayload
  | {
      type: 'template';
      template: DocSnapshot;
      bound: Bound | null;
    };

export type TemplateJobConfig = {
  model: SurfaceBlockModel;
  type: string;
  middlewares: ((job: TemplateJob) => void)[];
};

export class TemplateJob {
  static middlewares: ((job: TemplateJob) => void)[] = [];

  private _template: DocSnapshot | null = null;

  job: Transformer;

  model: SurfaceBlockModel;

  slots = {
    beforeInsert: new Subject<
      | SlotBlockPayload
      | {
          type: 'template';
          template: DocSnapshot;
          bound: Bound | null;
        }
    >(),
  };

  type: TemplateType;

  constructor({ model, type, middlewares }: TemplateJobConfig) {
    this.job = model.store.getTransformer();
    this.model = model;
    this.type = TEMPLATE_TYPES.includes(type as TemplateType)
      ? (type as TemplateType)
      : 'template';

    middlewares.forEach(middleware => middleware(this));
    TemplateJob.middlewares.forEach(middleware => middleware(this));
  }

  static create(options: {
    model: SurfaceBlockModel;
    type: string;
    middlewares: ((job: TemplateJob) => void)[];
  }) {
    return new TemplateJob(options);
  }

  private _getMergeBlockId(modelData: BlockSnapshot) {
    switch (modelData.flavour as MergeBlockFlavour) {
      case 'affine:page':
        return this.model.store.root!.id;
      case 'affine:surface':
        return this.model.id;
    }
  }

  private _getTemplateBound() {
    return this._template
      ? getBlockSnapshotContentBound([this._template.blocks])
      : null;
  }

  private _insertToDoc(
    modelDataList: {
      flavour: string;
      json: BlockSnapshot;
      modelData: SnapshotNode<object> | null;
      parent?: string;
      index?: number;
    }[]
  ) {
    const doc = this.model.store;
    const mergeIdMapping = new Map<string, string>();
    const deferInserting: typeof modelDataList = [];

    const insert = (
      data: (typeof modelDataList)[number],
      defered: boolean = true
    ) => {
      const { flavour, json, modelData, parent, index } = data;
      const isMergeBlock = MERGE_BLOCK.includes(flavour as MergeBlockFlavour);

      if (isMergeBlock) {
        mergeIdMapping.set(json.id, this._getMergeBlockId(json));
      }

      if (
        defered &&
        DEFERED_BLOCK.includes(flavour as (typeof DEFERED_BLOCK)[number])
      ) {
        deferInserting.push(data);
        return;
      } else {
        if (isMergeBlock) {
          this._mergeProps(
            json,
            this.model.store.getModelById(
              this._getMergeBlockId(json)
            ) as BlockModel
          );
          return;
        }

        if (!modelData) {
          return;
        }

        doc.addBlock(
          modelData.flavour,
          {
            ...modelData.props,
            id: modelData.id,
          },
          parent ? (mergeIdMapping.get(parent) ?? parent) : undefined,
          index
        );
      }
    };

    // Batch all inserts in ONE Y.js transaction for performance
    // Uses withoutTransact() to prevent inner addBlock() calls from creating their own transactions
    // This reduces O(n*m) to O(n+m) where n=existing blocks, m=new blocks
    const t0 = performance.now();
    console.log(
      `[TemplatePerf] _insertToDoc START: ${modelDataList.length} blocks (withoutTransact optimization)`
    );

    doc.transact(() => {
      console.log('[TemplatePerf] _insertToDoc: Outer transact() started');
      doc.withoutTransact(() => {
        console.log(
          '[TemplatePerf] _insertToDoc: withoutTransact() - inner transactions disabled'
        );
        modelDataList.forEach(data => insert(data));
        console.log(
          `[TemplatePerf] _insertToDoc: ${modelDataList.length} blocks inserted, ${deferInserting.length} deferred`
        );
        deferInserting.forEach(data => insert(data, false));
      });
      console.log('[TemplatePerf] _insertToDoc: withoutTransact() completed');
    });

    console.log(
      `[TemplatePerf] _insertToDoc DONE: ${(performance.now() - t0).toFixed(1)}ms`
    );
  }

  private async _jsonToModelData(json: BlockSnapshot) {
    const job = this.job;
    const defered: {
      snapshot: BlockSnapshot;
      parent?: string;
      index?: number;
    }[] = [];
    const modelDataList: {
      flavour: string;
      json: BlockSnapshot;
      modelData: SnapshotNode<object> | null;
      parent?: string;
      index?: number;
    }[] = [];
    const toModel = async (
      snapshot: BlockSnapshot,
      parent?: string,
      index?: number,
      defer: boolean = true
    ) => {
      if (
        defer &&
        DEFERED_BLOCK.includes(
          snapshot.flavour as (typeof DEFERED_BLOCK)[number]
        )
      ) {
        defered.push({
          snapshot,
          parent,
          index,
        });
        return;
      }

      const slotData = {
        blockJson: snapshot,
        parent,
        index,
      };

      this.slots.beforeInsert.next({ type: 'block', data: slotData });

      /**
       * merge block should not be converted to model data
       */
      const modelData = MERGE_BLOCK.includes(
        snapshot.flavour as MergeBlockFlavour
      )
        ? null
        : ((await job.snapshotToModelData(snapshot)) ?? null);

      modelDataList.push({
        flavour: snapshot.flavour,
        json: snapshot,
        modelData,
        parent,
        index,
      });

      if (snapshot.children) {
        let index = 0;
        for (const child of snapshot.children) {
          await toModel(child, snapshot.id, index);
          ++index;
        }
      }
    };

    await toModel(json);

    for (const json of defered) {
      await toModel(json.snapshot, json.parent, json.index, false);
    }

    return modelDataList;
  }

  private _mergeProps(from: BlockSnapshot, to: BlockModel) {
    switch (from.flavour as MergeBlockFlavour) {
      case 'affine:page':
        break;
      case 'affine:surface':
        this._mergeSurfaceElements(
          from.props.elements as Record<string, Record<string, unknown>>,
          to as SurfaceBlockModel
        );
        break;
    }
  }

  /**
   * The rectangle of an element of the template by its ID. Older templates
   * store no `xywh` for connectors and groups, since the editor derives it;
   * placing an element needs one, so it is taken from what the element spans:
   * a connector from the elements or blocks at its ends, a group from its
   * members.
   */
  private _snapshotRects(from: Record<string, Record<string, unknown>>) {
    const blockRects = new Map<string, Bound>();
    if (this._template)
      this.walk(block => {
        const xywh = block.props?.xywh;
        if (typeof xywh === 'string')
          blockRects.set(block.id, Bound.deserialize(xywh));
      });
    const resolved = new Map<string, Bound>();
    const rectOf = (id: string, path: readonly string[] = []): Bound => {
      const known = resolved.get(id) ?? blockRects.get(id);
      if (known) return known;
      const val = from[id];
      if (!val || path.includes(id))
        throw new PageStructureWriteError(
          `Template element ${id} has no rectangle to place it by`
        );
      const next = [...path, id];
      let bound: Bound | null;
      if (typeof val.xywh === 'string') {
        bound = Bound.deserialize(val.xywh);
      } else if (val.type === 'group') {
        bound = getCommonBound(
          snapshotMapKeys(val.children).map(member => rectOf(member, next))
        );
      } else if (val.type === 'connector') {
        bound = getCommonBound(
          [val.source, val.target].map(end => endRect(end, next))
        );
      } else bound = null;
      if (!bound)
        throw new PageStructureWriteError(
          `Template element ${id} has no rectangle to place it by`
        );
      resolved.set(id, bound);
      return bound;
    };
    const endRect = (end: unknown, path: readonly string[]): Bound => {
      const { id, position } = (end ?? {}) as {
        id?: string;
        position?: [number, number];
      };
      if (id) return rectOf(id, path);
      if (position) return new Bound(position[0], position[1], 0, 0);
      throw new PageStructureWriteError(
        `Template connector ${path.at(-1)} has an end without element or position`
      );
    };
    return (id: string) => rectOf(id).toXYWH();
  }

  private _mergeSurfaceElements(
    from: Record<string, Record<string, unknown>>,
    to: SurfaceBlockModel
  ) {
    const schema = this.model.store.schema.get('affine:surface');
    const surfaceTransformer = schema?.transformer?.(
      new Map()
    ) as SurfaceBlockTransformer;

    for (const val of Object.values(from)) {
      if (val.type !== 'mindmap') continue;
      for (const child of snapshotMapKeys(val.children))
        if (!from[child])
          throw new PageStructureWriteError('Template mindmap child missing');
    }
    this.model.store.transact(() => {
      const defered: [string, Record<string, unknown>][] = [];

      const rectOf = this._snapshotRects(from);
      const unitOf = new Map<string, string>();
      for (const [id, val] of Object.entries(from))
        if (val.type === 'mindmap')
          for (const child of snapshotMapKeys(val.children))
            unitOf.set(child, id);
      const place = (id: string, val: Record<string, unknown>) => {
        const rectangles =
          val.type === 'mindmap'
            ? snapshotMapKeys(val.children).map(
                child =>
                  surfaceTransformer.elementInput(child, from[child]).rect
              )
            : [];
        const x = Math.min(...rectangles.map(r => r[0]));
        const y = Math.min(...rectangles.map(r => r[1]));
        const rect = rectangles.length
          ? ([
              x,
              y,
              Math.max(...rectangles.map(r => r[0] + r[2])) - x,
              Math.max(...rectangles.map(r => r[1] + r[3])) - y,
            ] as const)
          : undefined;
        const own = rect ?? (val.xywh === undefined ? rectOf(id) : undefined);
        const input = surfaceTransformer.elementInput(
          id,
          own ? { ...val, xywh: JSON.stringify(own) } : val
        );
        this.model.store.placeElement(
          to.id,
          id,
          input.fields,
          input.order,
          rect ?? input.rect,
          unitOf.has(id) ? { mindmap: unitOf.get(id)! } : undefined
        );
        input.members.forEach(member =>
          this.model.store.pageStructure.join(member, id)
        );
      };

      const ordered = Object.entries(from);
      ordered.sort(
        ([, a], [, b]) =>
          Number(b.type === 'mindmap') - Number(a.type === 'mindmap')
      );
      ordered.forEach(([id, val]) => {
        if (['connector', 'group'].includes(val.type as string)) {
          defered.push([id, val]);
        } else {
          place(id, val);
        }
      });

      defered.forEach(([key, val]) => place(key, val));
    });
  }

  async insertTemplate(
    template: unknown,
    commit?: (insert: () => void) => void
  ) {
    DocSnapshotSchema.parse(template);

    assertType<DocSnapshot>(template);

    const assertTemplateBlock = (block: BlockSnapshot): void => {
      if (block.flavour === 'affine:database')
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          `Canvas templates cannot insert database blocks; ${ROW_SHARDED_DATABASE_GATE_MESSAGE}.`
        );
      for (const child of block.children ?? []) assertTemplateBlock(child);
    };
    // Validate the entire tree before conversion, asset writes or insertion slots.
    assertTemplateBlock(template.blocks);

    this._template = template;

    const templateBound = this._getTemplateBound();

    this.slots.beforeInsert.next({
      type: 'template',
      template: template,
      bound: templateBound,
    });

    const modelDataList = await this._jsonToModelData(template.blocks);

    await this.model.store.pageStructure.loadMissingPartsOf(
      modelDataList
        .filter(
          data => !MERGE_BLOCK.includes(data.flavour as MergeBlockFlavour)
        )
        .flatMap(data => (data.modelData ? [data.modelData.id] : []))
    );

    if (commit) commit(() => this._insertToDoc(modelDataList));
    else this._insertToDoc(modelDataList);

    return templateBound;
  }

  walk(callback: (block: BlockSnapshot, template: DocSnapshot) => void) {
    if (!this._template) {
      throw new Error('Template not loaded, please call insertTemplate first');
    }

    const iterate = (block: BlockSnapshot, template: DocSnapshot) => {
      callback(block, template);

      if (block.children) {
        block.children.forEach(child => iterate(child, template));
      }
    };

    iterate(this._template.blocks, this._template);
  }
}

/**
 * Where an inserted template goes, as the top left for its bound: dropped
 * at a point of the canvas, it is centred there; otherwise it goes to the
 * right of what the doc already shows, or stays where it was made when the
 * doc is empty (null).
 */
export function templatePlacement(
  contentBound: Bound | null,
  gap: number,
  droppedAt?: IVec
): TemplatePlace | null {
  if (droppedAt)
    return template => [
      droppedAt[0] - template.w / 2,
      droppedAt[1] - template.h / 2,
    ];
  if (!contentBound) return null;
  const right = createRightSidePlacementBound(contentBound, gap);
  return () => [right.x, right.y];
}

export type TemplateJobOptions = {
  /**
   * Where the inserted content comes from. 'catalog' (the dock's templates,
   * slides and stickers, named templates the AI inserts) marks its images as
   * catalogue art; 'content' (AI answers, generated slides) keeps them
   * ordinary images of the doc.
   */
  source: 'catalog' | 'content';
  /** The point of the canvas it was dropped at; see templatePlacement. */
  droppedAt?: IVec;
};

export function createTemplateJob(
  std: BlockStdScope,
  type: 'template' | 'sticker',
  { source, droppedAt }: TemplateJobOptions
) {
  const surface = getSurfaceBlock(std.store);
  if (!surface) {
    throw new BlockSuiteError(
      BlockSuiteError.ErrorCode.NoSurfaceModelError,
      'This doc is missing surface block in edgeless.'
    );
  }

  const gfx = std.get(GfxControllerIdentifier);
  const middlewares: ((job: TemplateJob) => void)[] = [];
  const { layer, viewport } = gfx;

  if (type === 'template') {
    const place = templatePlacement(
      getBlockSnapshotContentBound(std.store.root?.children ?? []),
      20 / viewport.zoom,
      droppedAt
    );
    if (place) middlewares.push(createInsertPlaceMiddleware(place));

    const idxGenerator = layer.createIndexGenerator();

    middlewares.push(createRegenerateIndexMiddleware(() => idxGenerator()));
  }

  if (type === 'sticker') {
    middlewares.push(
      createStickerMiddleware(
        droppedAt ? { x: droppedAt[0], y: droppedAt[1] } : viewport.center,
        () => layer.generateIndex()
      )
    );
  }

  if (source === 'catalog') middlewares.push(catalogImageMiddleware);
  middlewares.push(replaceIdMiddleware);

  return TemplateJob.create({
    model: surface,
    type,
    middlewares,
  });
}
